import axios from 'axios';

const DEFAULT_BASE_URL = 'https://epsilonskatingclub.com/api';

const normalizeBaseUrl = (url = DEFAULT_BASE_URL) => String(url).trim().replace(/\/+$/, '');

const configuredBaseUrl = normalizeBaseUrl(process.env.REACT_APP_BASE_URL || DEFAULT_BASE_URL);

export const SERVER_BASE_URL = configuredBaseUrl.endsWith('/api')
  ? configuredBaseUrl.slice(0, -4)
  : configuredBaseUrl;

export const API_BASE_URL = configuredBaseUrl.endsWith('/api')
  ? configuredBaseUrl
  : `${configuredBaseUrl}/api`;

export const buildApiUrl = (path = '') => {
  const normalizedPath = path
    ? path.startsWith('/')
      ? path
      : `/${path}`
    : '';

  return `${API_BASE_URL}${normalizedPath}`;
};

const api = axios.create({
  baseURL: API_BASE_URL,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  } else if (config.data) {
    config.headers['Content-Type'] = 'application/json';
  }
  return config;
});

export const getImageUrl = (imagePath) => {
  if (!imagePath) return '';
  if (imagePath.startsWith('http')) return imagePath;
  return `${SERVER_BASE_URL}${imagePath}`;
};

export const studentAPI = {
  getAll: () => api.get('/students'),
  getCurrent: () => api.get('/students/me'),
  getById: (id) => api.get(`/students/${id}`),
  create: (data) => api.post('/students', data),
  update: (id, data) => api.put(`/students/${id}`, data),
  delete: (id) => api.delete(`/students/${id}`),
};

export const teacherAPI = {
  getAll: () => api.get('/teachers'),
  getById: (id) => api.get(`/teachers/${id}`),
  create: (data) => api.post('/teachers', data),
  update: (id, data) => api.put(`/teachers/${id}`, data),
  delete: (id) => api.delete(`/teachers/${id}`),
};

export const productAPI = {
  getAll: () => api.get('/products'),
  getMyPurchases: () => api.get('/products/purchases/me'),
  getById: (id) => api.get(`/products/${id}`),
  purchase: (data) => api.post('/products/purchase', data),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
};

export const batchAPI = {
  getAll: () => api.get('/batches'),
  getById: (id) => api.get(`/batches/${id}`),
  create: (data) => api.post('/batches', data),
  update: (id, data) => api.put(`/batches/${id}`, data),
  delete: (id) => api.delete(`/batches/${id}`),
};

export const enrollmentAPI = {
  getAll: () => api.get('/enrollments'),
  getById: (id) => api.get(`/enrollments/${id}`),
  getByStudent: (studentId) => api.get(`/enrollments/student/${studentId}`),
  processDueSubscriptions: () => api.post('/enrollments/subscriptions/process-due'),
  create: (data) => api.post('/enrollments', data),
  update: (id, data) => api.put(`/enrollments/${id}`, data),
  delete: (id) => api.delete(`/enrollments/${id}`),
};

export const attendanceAPI = {
  getAll: () => api.get('/attendance'),
  getById: (id) => api.get(`/attendance/${id}`),
  getByStudent: (studentId) => api.get(`/attendance/student/${studentId}`),
  getByBatch: (batchId) => api.get(`/attendance/batch/${batchId}`),
  create: (data) => api.post('/attendance', data),
  update: (id, data) => api.put(`/attendance/${id}`, data),
  delete: (id) => api.delete(`/attendance/${id}`),
};

export const invoiceAPI = {
  getAll: () => api.get('/invoices'),
  getById: (id) => api.get(`/invoices/${id}`),
  getByStudent: (studentId) => api.get(`/invoices/student/${studentId}`),
  sendWhatsAppReminder: (studentId, data = {}) => api.post(`/invoices/student/${studentId}/send-whatsapp-reminder`, data),
  getSummary: (params) => api.get('/invoices/summary', { params }),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  delete: (id) => api.delete(`/invoices/${id}`),
};

export const invoiceItemAPI = {
  getAll: () => api.get('/invoice-items'),
  getById: (id) => api.get(`/invoice-items/${id}`),
  getByInvoice: (invoiceId) => api.get(`/invoice-items/invoice/${invoiceId}`),
  create: (data) => api.post('/invoice-items', data),
  update: (id, data) => api.put(`/invoice-items/${id}`, data),
  delete: (id) => api.delete(`/invoice-items/${id}`),
};

export const paymentAPI = {
  getAll: () => api.get('/payments'),
  getGatewayConfig: () => api.get('/payments/gateway-config'),
  getPendingFees: (params) => api.get('/payments/pending-fees', { params }),
  getById: (id) => api.get(`/payments/${id}`),
  getByInvoice: (invoiceId) => api.get(`/payments/invoice/${invoiceId}`),
  getSummary: (params) => api.get('/payments/summary', { params }),
  createCheckoutOrder: (data) => api.post('/payments/checkout/order', data),
  verifyCheckoutPayment: (data) => api.post('/payments/checkout/verify', data),
  sendInvoiceReminder: (invoiceId, data) => api.post(`/payments/invoice/${invoiceId}/reminder`, data),
  create: (data) => api.post('/payments', data),
  update: (id, data) => api.put(`/payments/${id}`, data),
  delete: (id) => api.delete(`/payments/${id}`),
};

export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  getStudentProfile: () => api.get('/auth/user/profile'),
  updateStudentProfile: (data) => api.put('/auth/user/profile', data),
  getTeacherProfile: () => api.get('/auth/teacher/profile'),
  updateTeacherProfile: (data) => api.put('/teachers/me', data),
  requestPasswordReset: (data) => api.post('/auth/password/forgot', data),
  resetPassword: (data) => api.post('/auth/password/reset', data),
};

export default api;
